/**
 * Tracing and simplification
 *
 * Walks the skeleton into ordered polylines, rejoins the pieces a junction split
 * apart, simplifies with Ramer–Douglas–Peucker, and measures each curve: length,
 * depth, curvature, closure, branches.
 *
 * These measurements — not the pixels — are what the reader interprets.
 *
 * A polyline is `{ points: [[x,y],…], closed: boolean }`.
 *
 * © Manish Jagdish Thatte
 */

import { countNeighbours, crossings } from './skeletonize.js';

const N8 = [[0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1]];

/** Junction pixels (≥3 neighbours), with adjacent ones merged into one centroid each. */
export function junctions(skeleton) {
  const { width: w, height: h, data } = skeleton;
  const isJ = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (data[y * w + x] && crossings(skeleton, x, y) >= 3) isJ[y * w + x] = 1;
  }
  const seen = new Uint8Array(w * h); const out = [];
  for (let i = 0; i < isJ.length; i++) {
    if (!isJ[i] || seen[i]) continue;
    const stack = [i]; seen[i] = 1; let sx = 0; let sy = 0; let n = 0;
    while (stack.length) {
      const p = stack.pop(); const px = p % w; const py = (p - px) / w; sx += px; sy += py; n++;
      for (const [dx, dy] of N8) {
        const xx = px + dx; const yy = py + dy;
        if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
        const q = yy * w + xx; if (isJ[q] && !seen[q]) { seen[q] = 1; stack.push(q); }
      }
    }
    out.push([sx / n, sy / n]);
  }
  return out;
}

/** Ramer–Douglas–Peucker. */
export function simplify(points, epsilon = 1.5) {
  if (points.length < 3) return points.map((p) => [...p]);
  const keep = new Uint8Array(points.length); keep[0] = 1; keep[points.length - 1] = 1;
  const stack = [[0, points.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop();
    const [ax, ay] = points[a]; const [bx, by] = points[b];
    const dx = bx - ax; const dy = by - ay; const len = Math.hypot(dx, dy) || 1;
    let maxD = 0; let idx = -1;
    for (let i = a + 1; i < b; i++) {
      const d = Math.abs(dy * points[i][0] - dx * points[i][1] + bx * ay - by * ax) / len;
      if (d > maxD) { maxD = d; idx = i; }
    }
    if (maxD > epsilon && idx > 0) { keep[idx] = 1; stack.push([a, idx], [idx, b]); }
  }
  return points.filter((_, i) => keep[i]).map((p) => [...p]);
}

/**
 * Trace a skeleton into polylines, split at junctions and endpoints. Closed
 * loops with no endpoint or junction are returned with `closed: true`.
 */
export function trace(skeleton) {
  const { width: w, height: h, data } = skeleton;
  // A node is an endpoint (one neighbour) or a true branch point (three or more
  // separate arms — the crossing number, not the neighbour count, because a
  // pixel on a diagonal run can touch three pixels of the *same* arm).
  const deg = new Int8Array(w * h); const cross = new Int8Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (data[y * w + x]) {
    deg[y * w + x] = countNeighbours(skeleton, x, y); cross[y * w + x] = crossings(skeleton, x, y);
  }
  const isNode = (i) => data[i] && (deg[i] === 1 || cross[i] >= 3);
  const usedEdge = new Set();
  const key = (a, b) => (a < b ? `${a}:${b}` : `${b}:${a}`);
  const visited = new Uint8Array(w * h);
  const out = [];

  const neigh = (i) => {
    const x = i % w; const y = (i - x) / w; const r = [];
    for (const [dx, dy] of N8) {
      const xx = x + dx; const yy = y + dy;
      if (xx >= 0 && yy >= 0 && xx < w && yy < h && data[yy * w + xx]) r.push(yy * w + xx);
    }
    return r;
  };
  const pt = (i) => [i % w, Math.floor(i / w)];

  for (let s = 0; s < data.length; s++) {
    if (!isNode(s)) continue;
    for (const first of neigh(s)) {
      if (usedEdge.has(key(s, first))) continue;
      const path = [s]; let prev = s; let cur = first; usedEdge.add(key(s, first));
      for (;;) {
        path.push(cur); visited[cur] = 1;
        if (isNode(cur)) break;
        const cand = neigh(cur).filter((q) => q !== prev && !usedEdge.has(key(cur, q)));
        if (!cand.length) break;
        // reach a node if one is adjacent; otherwise prefer an arm that does not hug the pixel we came from
        const px = prev % w; const py = Math.floor(prev / w);
        const nx = cand.find((q) => isNode(q)) ?? cand.find((q) => Math.max(Math.abs((q % w) - px), Math.abs(Math.floor(q / w) - py)) > 1) ?? cand[0];
        usedEdge.add(key(cur, nx)); prev = cur; cur = nx;
      }
      visited[s] = 1;
      if (path.length >= 2) out.push({ points: path.map(pt), closed: false });
    }
  }
  // pure loops
  for (let s = 0; s < data.length; s++) {
    if (!data[s] || visited[s]) continue;
    const path = [s]; visited[s] = 1; let prev = -1; let cur = s;
    for (;;) {
      const nx = neigh(cur).find((q) => q !== prev && !visited[q]);
      if (nx === undefined) break;
      path.push(nx); visited[nx] = 1; prev = cur; cur = nx;
    }
    if (path.length >= 4) out.push({ points: path.map(pt), closed: true });
  }
  return out;
}

function headingAt(points, atStart, span = 6) {
  const n = points.length; const a = atStart ? points[0] : points[n - 1];
  const b = atStart ? points[Math.min(n - 1, span)] : points[Math.max(0, n - 1 - span)];
  const dx = a[0] - b[0]; const dy = a[1] - b[1]; const l = Math.hypot(dx, dy) || 1;
  return [dx / l, dy / l];                           // pointing outward from the polyline
}

/**
 * Rejoin the pieces a junction split apart: at each junction, pair the two
 * segment-ends that continue most nearly straight through it (outward headings
 * roughly opposite). A rekhā that a small branch crosses stays one line.
 */
export function mergeStraight(polylines, { radius = 3, maxDot = -0.6 } = {}) {
  let segs = polylines.map((p) => ({ ...p, points: p.points.map((q) => [...q]) }));
  for (let guard = 0; guard < 200; guard++) {
    let best = null;
    const ends = [];
    segs.forEach((s, si) => {
      if (s.closed) return;
      ends.push({ si, start: true, p: s.points[0] }, { si, start: false, p: s.points[s.points.length - 1] });
    });
    for (let i = 0; i < ends.length; i++) for (let j = i + 1; j < ends.length; j++) {
      const a = ends[i]; const b = ends[j]; if (a.si === b.si) continue;
      if (Math.hypot(a.p[0] - b.p[0], a.p[1] - b.p[1]) > radius) continue;
      const ha = headingAt(segs[a.si].points, a.start); const hb = headingAt(segs[b.si].points, b.start);
      const dot = ha[0] * hb[0] + ha[1] * hb[1];
      if (dot < maxDot && (!best || dot < best.dot)) best = { a, b, dot };
    }
    if (!best) break;
    const { a, b } = best;
    let pa = segs[a.si].points.slice(); let pb = segs[b.si].points.slice();
    if (a.start) pa.reverse();                       // make a's joined end last
    if (!b.start) pb.reverse();                      // make b's joined end first
    const merged = { points: pa.concat(pb), closed: false };
    segs = segs.filter((_, k) => k !== a.si && k !== b.si); segs.push(merged);
  }
  return segs;
}

/** Polyline length in pixels. */
export function lengthOf(points) {
  let l = 0; for (let i = 1; i < points.length; i++) l += Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]);
  return l;
}

/** Point at fraction `t` ∈ [0,1] of arc length. */
export function pointAt(points, t) {
  const total = lengthOf(points); if (!total) return [...points[0]];
  let target = Math.min(1, Math.max(0, t)) * total;
  for (let i = 1; i < points.length; i++) {
    const seg = Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]);
    if (target <= seg || i === points.length - 1) {
      const f = seg ? Math.min(1, target / seg) : 0;
      return [points[i - 1][0] + (points[i][0] - points[i - 1][0]) * f, points[i - 1][1] + (points[i][1] - points[i - 1][1]) * f];
    }
    target -= seg;
  }
  return [...points[points.length - 1]];
}

/**
 * Measure a polyline. `gray` (optional) is the luminance image used for `depth`:
 * how much darker the line is than the skin a few pixels to either side of it.
 *
 * Returns `{ length, chord, straightness, turning, curvature, depth, closed, bbox, start, end }`.
 * `turning` is the signed total change of heading in radians (positive =
 * clockwise in image coordinates); `curvature` is |turning| per pixel.
 */
export function measure(polyline, gray = null) {
  const pts = polyline.points ?? polyline;
  const length = lengthOf(pts);
  const start = pts[0]; const end = pts[pts.length - 1];
  const chord = Math.hypot(end[0] - start[0], end[1] - start[1]);
  const s = simplify(pts, 2);
  let turning = 0;
  for (let i = 2; i < s.length; i++) {
    const a1 = Math.atan2(s[i - 1][1] - s[i - 2][1], s[i - 1][0] - s[i - 2][0]);
    const a2 = Math.atan2(s[i][1] - s[i - 1][1], s[i][0] - s[i - 1][0]);
    let d = a2 - a1; while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI;
    turning += d;
  }
  let depth = null;
  if (gray) {
    let sum = 0; let n = 0;
    for (let i = 1; i < pts.length - 1; i++) {
      const dx = pts[i + 1][0] - pts[i - 1][0]; const dy = pts[i + 1][1] - pts[i - 1][1]; const l = Math.hypot(dx, dy) || 1;
      const nx = -dy / l; const ny = dx / l;
      const at = (x, y) => gray.data[Math.min(gray.height - 1, Math.max(0, Math.round(y))) * gray.width + Math.min(gray.width - 1, Math.max(0, Math.round(x)))];
      const side = (at(pts[i][0] + nx * 4, pts[i][1] + ny * 4) + at(pts[i][0] - nx * 4, pts[i][1] - ny * 4)) / 2;
      sum += side - at(pts[i][0], pts[i][1]); n++;
    }
    depth = n ? sum / n : 0;
  }
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  for (const [x, y] of pts) { minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y); }
  return {
    length, chord, straightness: length ? chord / length : 1, turning,
    curvature: length ? Math.abs(turning) / length : 0, depth, closed: !!polyline.closed,
    bbox: { minX, minY, maxX, maxY }, start, end
  };
}

/**
 * Split a polyline where it turns sharply (more than `minTurn` radians, on the
 * RDP-simplified path). Two different lines that meet at a point — a head line
 * leaving the start of the life line — trace as one path with a corner in it;
 * a single rekhā curves smoothly and does not.
 */
export function splitAtCorners(polyline, { minTurn = (65 * Math.PI) / 180, epsilon = 2.5, minPiece = 6 } = {}) {
  const pts = polyline.points;
  if (polyline.closed || pts.length < 5) return [polyline];
  const s = simplify(pts, epsilon);
  if (s.length < 3) return [polyline];
  const cut = [];
  for (let i = 1; i < s.length - 1; i++) {
    const a1 = Math.atan2(s[i][1] - s[i - 1][1], s[i][0] - s[i - 1][0]);
    const a2 = Math.atan2(s[i + 1][1] - s[i][1], s[i + 1][0] - s[i][0]);
    let d = Math.abs(a2 - a1); if (d > Math.PI) d = 2 * Math.PI - d;
    if (d > minTurn) cut.push(s[i]);
  }
  if (!cut.length) return [polyline];
  const out = []; let cur = [];
  const isCut = (p) => cut.some((c) => c[0] === p[0] && c[1] === p[1]);
  for (const p of pts) {
    cur.push(p);
    if (isCut(p)) { if (cur.length >= minPiece) out.push({ points: cur, closed: false }); cur = [p]; }
  }
  if (cur.length >= 2 && (cur.length >= minPiece || !out.length)) out.push({ points: cur, closed: false });
  return out.length ? out : [polyline];
}
